import React, { useMemo, useState } from 'react';
import { Pagination, Table, TableSkeleton, TD, TH, THead, TR } from '../ui/Table';
import { SearchInput } from '../ui/SearchInput';
import { Select } from '../ui/Select';
import { MultiSelect } from '../ui/MultiSelect';
import { SeatsIndicator } from '../ui/StatusPill';
import { deviceTypeOptions, planDeviceType, planSlug, planTypeLabel, schoolFilterOptions, schoolName } from '../../data/district';
import { optInPlanRows, groupPlanRows } from '../../data/plans';
import { usePagination } from '../../hooks/usePagination';
const typeOptions = deviceTypeOptions.filter(t => t !== 'All Device Types') as string[];
export function PlansTab({
  program,
  loading
}: {
  program: 'optin' | 'group';
  loading: boolean;
}) {
  const [query, setQuery] = useState('');
  const [school, setSchool] = useState('All Schools');
  const [types, setTypes] = useState<string[]>([]);
  const optInRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return optInPlanRows.filter(row => {
      if (school !== 'All Schools' && schoolName(row.schoolSlug) !== school) return false;
      if (types.length > 0 && !types.includes(planDeviceType(row.planId))) return false;
      if (!q) return true;
      return `${planSlug(row.planId)} ${row.studentName} ${row.studentId}`.toLowerCase().includes(q);
    });
  }, [query, school, types]);
  const groupRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groupPlanRows.filter(row => {
      if (types.length > 0 && !types.includes(row.deviceTypeLabel === 'iPad' ? 'Tablet' : 'Laptop')) {
        return false;
      }
      if (!q) return true;
      return `${row.planName} ${row.deviceTypeLabel}`.toLowerCase().includes(q);
    });
  }, [query, types]);
  const optIn = usePagination(optInRows, 15);
  const group = usePagination(groupRows, 15);
  const active = program === 'optin' ? optIn : group;
  return <div className="flex flex-1 flex-col" data-id="element-164" data-mp="kpurwssq3xhy">
      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-165" data-mp="rrrn5nekipyg">
        {program === 'optin' && <Select options={schoolFilterOptions} value={school} placeholder="All Schools" onChange={setSchool} data-id="element-166" data-mp="wqugwbp3l9r4" />}
        <MultiSelect options={typeOptions} allLabel="All Device Types" selected={types} onChange={setTypes} data-id="element-167" data-mp="11pe5ykyzn1np" />
        <SearchInput value={query} onChange={setQuery} placeholder="Search plans by name" className="w-[210px]" data-id="element-168" data-mp="16gw4l2djgq5i" />
      </div>

      {program === 'optin' ? <Table minWidth={1180} data-id="element-169" data-mp="1fgaw855fz6rl">
          <THead data-id="element-170" data-mp="1ffhn4xj0g56a">
            <tr data-id="element-171" data-mp="1fj5gnwoggrl0">
              <TH data-id="element-172" data-mp="1kgou4udbx0bw">Plan Name</TH>
              <TH data-id="element-173" data-mp="1khgmlwbmqhxy">Device Type</TH>
              <TH data-id="element-174" data-mp="1kecdckjb4w0f">School</TH>
              <TH data-id="element-175" data-mp="1pf8hmz7kmk6d">Student Name</TH>
              <TH data-id="element-176" data-mp="1pegso78hbkp2">Student Email</TH>
              <TH data-id="element-177" data-mp="1pdfi03vdl07v">Student ID</TH>
            </tr>
          </THead>
          {loading ? <TableSkeleton columns={6} data-id="element-178" data-mp="3m9mudb9fqv6v" /> : <tbody data-id="element-179" data-mp="3r63gy10zl1hu">
              {optIn.pageItems.length === 0 && <TR data-id="element-180" data-mp="28bqvlk7nd5ha">
                  <TD colSpan={6} className="py-10 text-center text-ink-500" data-id="element-181" data-mp="28c0jn294t2kc">
                    No plans found
                  </TD>
                </TR>}
              {optIn.pageItems.map(row => <TR key={row.id} data-id="element-182" data-mp="1zk8dh3r54flk">
                  <TD data-id="element-183" data-mp="1zhcct7nwrctf">{planSlug(row.planId)}</TD>
                  <TD data-id="element-184" data-mp="1ul5arpymulgr">{planTypeLabel(row.planId)}</TD>
                  <TD data-id="element-185" data-mp="1uiizpgcj9nts">{schoolName(row.schoolSlug)}</TD>
                  <TD data-id="element-186" data-mp="2r9l6y4w8zt2u">{row.studentName}</TD>
                  <TD data-id="element-187" data-mp="2r8a5ibq9yxew">{row.studentEmail}</TD>
                  <TD data-id="element-188" data-mp="2na2pxk7js25a">{row.studentId}</TD>
                </TR>)}
            </tbody>}
        </Table> : <Table minWidth={1180} data-id="element-189" data-mp="2i83nccjmt99e">
          <THead data-id="element-190" data-mp="2db3r0tcl3kvh">
            <tr data-id="element-191" data-mp="2dbmxndwsamu7">
              <TH data-id="element-192" data-mp="2d9seo552p4sr">Plan Name</TH>
              <TH data-id="element-193" data-mp="2dcy1teu55wlu">Effective Start Date</TH>
              <TH data-id="element-194" data-mp="16vayb9i86yx1">Effective End Date</TH>
              <TH data-id="element-195" data-mp="16wch605hgppb">Device Type</TH>
              <TH data-id="element-196" data-mp="11voicsb0j4kj">Available Seats</TH>
            </tr>
          </THead>
          {loading ? <TableSkeleton columns={5} rows={2} data-id="element-197" data-mp="2pse9f6s38mby" /> : <tbody data-id="element-198" data-mp="2pqth7nt8xfh9">
              {group.pageItems.length === 0 && <TR data-id="element-199" data-mp="2uk4ofq1yzy9p">
                  <TD colSpan={5} className="py-10 text-center text-ink-500" data-id="element-200" data-mp="2uknvgbq3128y">
                    No plans found
                  </TD>
                </TR>}
              {group.pageItems.map(row => <TR key={row.id} data-id="element-201" data-mp="2loonkp2ssium">
                  <TD data-id="element-202" data-mp="39gn35l9a50dk">{row.planName}</TD>
                  <TD data-id="element-203" data-mp="39gveh8pr7xmn">{row.startDate}</TD>
                  <TD data-id="element-204" data-mp="3dhyrzokbxhkp">{row.endDate}</TD>
                  <TD data-id="element-205" data-mp="3dgxivpmvj55m">{row.deviceTypeLabel}</TD>
                  <TD data-id="element-206" data-mp="3dfmcrj5yhlbc">
                    <SeatsIndicator used={row.seatsUsed} total={row.seatsTotal} data-id="element-207" data-mp="2zifurqt23qn6" />
                  </TD>
                </TR>)}
            </tbody>}
        </Table>}
      <Pagination page={active.page} totalPages={active.totalPages} onChange={active.setPage} data-id="element-208" data-mp="1o3aibv34idqd" />
    </div>;
}